import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { UserPlus, Edit, Trash2, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TAB_PERMISSIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'budget_upload', label: 'Budget Upload' },
  { key: 'purchase_orders', label: 'Purchase Orders' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'bank', label: 'Bank' },
  { key: 'approvals', label: 'Approvals' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'ai', label: 'AI Assistant' },
  { key: 'email_hub', label: 'Email Hub' },
  { key: 'accounting', label: 'Accounting' },
  { key: 'reports', label: 'Reports' },
  { key: 'alerts', label: 'Alerts' },
  { key: 'settings', label: 'Settings' },
];

// Mirror of backend ROLE_PRESETS — kept in sync so the Advanced toggles can
// pre-fill correctly when the admin picks a preset and then customises.
const ROLE_PRESETS = {
  viewer: {
    label: 'Viewer',
    description: 'Read-only access to all finance tabs. Cannot create or edit.',
    is_read_only: true,
    tab_permissions: {
      dashboard: true, budget_upload: false, purchase_orders: true, invoices: true,
      vendors: true, bank: true, approvals: true, revenue: true,
      ai: false, email_hub: false, accounting: true, reports: true,
      alerts: true, settings: false,
    },
  },
  accountant: {
    label: 'Accountant',
    description: 'Full read+write across all operational tabs. No settings access.',
    is_read_only: false,
    tab_permissions: {
      dashboard: true, budget_upload: false, purchase_orders: true, invoices: true,
      vendors: true, bank: true, approvals: true, revenue: true,
      ai: true, email_hub: true, accounting: true, reports: true,
      alerts: true, settings: false,
    },
  },
  admin: {
    label: 'Admin',
    description: 'Full access including budget uploads and settings.',
    is_read_only: false,
    tab_permissions: {
      dashboard: true, budget_upload: true, purchase_orders: true, invoices: true,
      vendors: true, bank: true, approvals: true, revenue: true,
      ai: true, email_hub: true, accounting: true, reports: true,
      alerts: true, settings: true,
    },
  },
};

const DEFAULT_INVITE_STATE = {
  email: '',
  name: '',
  role_preset: 'accountant', // sensible default
  is_read_only: false,
  tab_permissions: { ...ROLE_PRESETS.accountant.tab_permissions },
  showAdvanced: false,
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const [inviteData, setInviteData] = useState({ ...DEFAULT_INVITE_STATE });

  // Invite result (link + email-delivery status). Lets the admin copy the
  // magic link manually if email delivery falls back.
  const [inviteResult, setInviteResult] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        withCredentials: true,
      });
      setUsers(response.data.users);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        email: inviteData.email,
        name: inviteData.name,
        role_preset: inviteData.role_preset || null,
        // Only send explicit overrides when the admin opened Advanced and
        // edited individual toggles — otherwise let the backend apply the
        // preset cleanly.
        ...(inviteData.showAdvanced
          ? {
              tab_permissions: inviteData.tab_permissions,
              is_read_only: !!inviteData.is_read_only,
              role_preset: null, // override wins
            }
          : {}),
      };
      const response = await axios.post(
        `${API}/users/invite`,
        payload,
        { withCredentials: true }
      );

      setInviteResult({
        email: inviteData.email,
        name: inviteData.name,
        email_sent: !!response.data.email_sent,
        link: response.data.set_password_link,
      });

      if (response.data.email_sent) {
        toast.success(`Invite email sent to ${inviteData.email}`);
      } else {
        toast.info('User created. Email delivery failed — share the link manually.');
      }

      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    }
  };

  const resetInviteForm = () => {
    setInviteData({ ...DEFAULT_INVITE_STATE });
    setInviteResult(null);
    setInviteOpen(false);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/users/${selectedUser.user_id}`,
        {
          name: selectedUser.name,
          tab_permissions: selectedUser.tab_permissions,
          is_active: selectedUser.is_active,
          is_read_only: !!selectedUser.is_read_only,
        },
        { withCredentials: true }
      );
      
      toast.success('User updated successfully');
      setEditOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`, {
        withCredentials: true,
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (userId, userEmail) => {
    try {
      const response = await axios.post(
        `${API}/users/${userId}/reset-password`,
        {},
        { withCredentials: true }
      );
      if (response.data.email_sent) {
        toast.success(`Password reset link emailed to ${userEmail}`);
      } else {
        // Surface the link as a fallback so super-admin can deliver manually.
        const link = response.data.set_password_link;
        try {
          await navigator.clipboard.writeText(link);
          toast.info('Email delivery failed — magic link copied to clipboard. Share it manually.');
        } catch {
          toast.info(`Email delivery failed. Magic link: ${link}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser({ ...user });
    setEditOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">
            User Management
          </h1>
          <p className="text-muted-foreground">
            Manage users and their access permissions
          </p>
        </div>
        
        <Dialog open={inviteOpen} onOpenChange={(o) => { if (!o) resetInviteForm(); else setInviteOpen(true); }}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-button" data-write-only>
              <UserPlus className="h-4 w-4 mr-2" />
              Invite User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Invite New User</DialogTitle>
            </DialogHeader>

            {inviteResult ? (
              <div className="space-y-4" data-testid="invite-result">
                <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4">
                  <div className="font-semibold text-emerald-900">
                    {inviteResult.email_sent ? 'Invite email sent' : 'User created'}
                  </div>
                  <div className="text-sm text-emerald-900/80 mt-1">
                    {inviteResult.email_sent
                      ? `${inviteResult.name} (${inviteResult.email}) will receive a magic link to set their password. The link expires in 48 hours.`
                      : `Email delivery failed. Share the link below with ${inviteResult.name} (${inviteResult.email}) manually. The link expires in 48 hours.`}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>One-time set-password link</Label>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={inviteResult.link || ''}
                      className="font-mono text-xs"
                      data-testid="invite-magic-link"
                      onFocus={(e) => e.target.select()}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(inviteResult.link || '');
                          toast.success('Link copied to clipboard');
                        } catch {
                          toast.error('Could not copy — select and copy manually');
                        }
                      }}
                      data-testid="invite-copy-link-button"
                    >
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Single-use. Once the user sets their password, this link is invalidated automatically.
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={resetInviteForm}>Close</Button>
                  <Button
                    onClick={() => {
                      setInviteResult(null);
                      setInviteData({ ...DEFAULT_INVITE_STATE });
                    }}
                  >
                    Invite another user
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleInvite} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite-name">Full Name</Label>
                    <Input
                      id="invite-name"
                      data-testid="invite-name-input"
                      value={inviteData.name}
                      onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invite-email">Email</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      data-testid="invite-email-input"
                      value={inviteData.email}
                      onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                {/* Role preset — 3 simple roles instead of 14 toggles. */}
                <div className="space-y-2">
                  <Label>Role</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(ROLE_PRESETS).map(([key, p]) => {
                      const active = inviteData.role_preset === key && !inviteData.showAdvanced;
                      return (
                        <button
                          key={key}
                          type="button"
                          data-testid={`invite-preset-${key}`}
                          onClick={() => setInviteData({
                            ...inviteData,
                            role_preset: key,
                            is_read_only: p.is_read_only,
                            tab_permissions: { ...p.tab_permissions },
                            showAdvanced: false,
                          })}
                          className={`text-left rounded-lg border p-3 transition ${
                            active
                              ? 'border-accent bg-accent/10 ring-1 ring-accent'
                              : 'border-border hover:border-foreground/30'
                          }`}
                        >
                          <div className="font-semibold text-sm">{p.label}</div>
                          <div className="text-xs text-muted-foreground mt-1 leading-snug">
                            {p.description}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Advanced — only shown on demand. Keeps the 14-toggle grid
                    available for edge-case permission setups without
                    cluttering the default flow. */}
                <div className="rounded-md border border-border bg-muted/20">
                  <button
                    type="button"
                    data-testid="invite-toggle-advanced"
                    onClick={() => setInviteData({ ...inviteData, showAdvanced: !inviteData.showAdvanced })}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium hover:bg-muted/40 transition"
                  >
                    <span>Advanced — custom permissions</span>
                    <span className="text-xs text-muted-foreground">
                      {inviteData.showAdvanced ? 'Hide' : 'Show'}
                    </span>
                  </button>
                  {inviteData.showAdvanced && (
                    <div className="border-t border-border/60 p-3 space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        {TAB_PERMISSIONS.map((tab) => (
                          <div key={tab.key} className="flex items-center justify-between px-3 py-2 border rounded-md bg-background">
                            <Label htmlFor={`invite-${tab.key}`} className="cursor-pointer text-sm">
                              {tab.label}
                            </Label>
                            <Switch
                              id={`invite-${tab.key}`}
                              checked={!!inviteData.tab_permissions[tab.key]}
                              onCheckedChange={(checked) =>
                                setInviteData({
                                  ...inviteData,
                                  tab_permissions: {
                                    ...inviteData.tab_permissions,
                                    [tab.key]: checked,
                                  },
                                })
                              }
                            />
                          </div>
                        ))}
                      </div>
                      <div
                        className="flex items-center justify-between p-3 border rounded-md bg-amber-50/40"
                        data-testid="invite-read-only-block"
                      >
                        <div>
                          <Label htmlFor="invite-read-only" className="cursor-pointer font-semibold text-sm">
                            Read-only viewer
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Can view the tabs above but cannot create, edit, or delete records.
                          </p>
                        </div>
                        <Switch
                          id="invite-read-only"
                          data-testid="invite-read-only-toggle"
                          checked={!!inviteData.is_read_only}
                          onCheckedChange={(checked) =>
                            setInviteData({ ...inviteData, is_read_only: checked })
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-md bg-muted/30 border border-border/60 p-3 text-xs text-muted-foreground leading-relaxed">
                  The user will receive an email with a one-time link to set their own password.
                  No password will be displayed here. The link expires in 48 hours.
                </div>

                <Button type="submit" className="w-full" data-testid="invite-submit-button">
                  Send Invitation
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Access</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge variant={user.is_super_admin ? 'default' : 'secondary'}>
                      {user.is_super_admin ? 'Super Admin' : 'Normal User'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.is_super_admin ? (
                      <Badge variant="outline">Full Access</Badge>
                    ) : user.is_read_only ? (
                      <Badge variant="outline" className="border-amber-300 text-amber-700">
                        Read-only
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                        Read &amp; Write
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.is_active ? 'success' : 'destructive'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {!user.is_super_admin && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(user)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResetPassword(user.user_id, user.email)}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete User</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete {user.name}? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteUser(user.user_id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Activity Log — last 100 audit-trail events (logins, logouts, writes)
          across all users. Super-admin only. Helps you see who changed what
          and when, and verify the read-only guard actually fired on a viewer. */}
      <ActivityLogCard />

      {/* Edit User Dialog */}
      {selectedUser && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit User: {selectedUser.name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Full Name</Label>
                <Input
                  id="edit-name"
                  value={selectedUser.name}
                  onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                  required
                />
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <Label htmlFor="edit-active">Active Status</Label>
                <Switch
                  id="edit-active"
                  checked={selectedUser.is_active}
                  onCheckedChange={(checked) => 
                    setSelectedUser({ ...selectedUser, is_active: checked })
                  }
                />
              </div>

              <div
                className="flex items-center justify-between p-3 border rounded-lg bg-amber-50/40"
                data-testid="edit-read-only-block"
              >
                <div>
                  <Label htmlFor="edit-read-only" className="cursor-pointer font-semibold">
                    Read-only viewer
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Can view the tabs below but cannot create, edit, or delete records.
                  </p>
                </div>
                <Switch
                  id="edit-read-only"
                  data-testid="edit-read-only-toggle"
                  checked={!!selectedUser.is_read_only}
                  onCheckedChange={(checked) =>
                    setSelectedUser({ ...selectedUser, is_read_only: checked })
                  }
                />
              </div>
              
              <div className="space-y-3">
                <Label>Tab Permissions</Label>
                <div className="grid grid-cols-2 gap-3">
                  {TAB_PERMISSIONS.map((tab) => (
                    <div key={tab.key} className="flex items-center justify-between p-3 border rounded-lg">
                      <Label htmlFor={`edit-${tab.key}`} className="cursor-pointer">
                        {tab.label}
                      </Label>
                      <Switch
                        id={`edit-${tab.key}`}
                        checked={selectedUser.tab_permissions?.[tab.key] || false}
                        onCheckedChange={(checked) => 
                          setSelectedUser({
                            ...selectedUser,
                            tab_permissions: {
                              ...selectedUser.tab_permissions,
                              [tab.key]: checked
                            }
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              <Button type="submit" className="w-full">
                Update User
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default UserManagement;


// ────────────────────────────────────────────────────────────────────
// Activity Log — read-only audit feed.
// Kept inline because it shares the page's data-flow and renders below
// the users table. If we ever expose it on another page, lift this out.
// ────────────────────────────────────────────────────────────────────
const ACTION_TONES = {
  login: 'bg-emerald-50 text-emerald-700',
  logout: 'bg-slate-100 text-slate-600',
  create: 'bg-blue-50 text-blue-700',
  update: 'bg-amber-50 text-amber-700',
  delete: 'bg-red-50 text-red-700',
};

const ActivityLogCard = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');

  const fetchLog = async (action = '') => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (action) params.action = action;
      const res = await axios.get(`${API}/users/activity-log`, {
        params,
        withCredentials: true,
      });
      setEntries(res.data.entries || []);
    } catch (e) {
      console.warn('activity log fetch failed', e?.response?.data || e?.message);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLog(filterAction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAction]);

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  return (
    <Card data-testid="activity-log-card">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle>Activity Log</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Latest 100 audit-trail events across all users
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            data-testid="activity-log-action-filter"
          >
            <option value="">All actions</option>
            <option value="login">Login</option>
            <option value="logout">Logout</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchLog(filterAction)}
            data-testid="activity-log-refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            No activity recorded yet.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id} data-testid={`activity-log-row-${e.id}`}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {fmtDate(e.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{e.name || e.email}</div>
                    <div className="text-[11px] text-muted-foreground">{e.email}</div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${
                        ACTION_TONES[e.action] || 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {e.action}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {e.entity}
                    {e.entity_id ? (
                      <span className="ml-1 text-[10px] tabular-nums">#{String(e.entity_id).slice(0, 8)}</span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-[11px] text-muted-foreground max-w-[280px] truncate" title={JSON.stringify(e.details)}>
                    {e.details && Object.keys(e.details).length > 0
                      ? Object.entries(e.details).slice(0, 3).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
